import React, { useState, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  VStack,
  Textarea,
  Badge,
  HStack,
  Heading,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { useGetScriptQuery, useUpdateScriptMutation } from "services/api";
import debounce from "lodash/debounce";

const Script = () => {
  const { projectId } = useParams();
  const [scriptContent, setScriptContent] = useState("");
  const { data: script, isLoading } = useGetScriptQuery({ projectId });
  const [updateScript] = useUpdateScriptMutation();
  const [saveStatus, setSaveStatus] = useState({ message: "", type: "" });
  const saveTimeoutRef = useRef(null);

  // Initialize script content when data is loaded
  useEffect(() => {
    if (script) {
      setScriptContent(script);
    }
  }, [script]);

  // Clear save status after delay
  useEffect(() => {
    if (saveStatus.message) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus({ message: "", type: "" });
      }, 2000);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [saveStatus]);

  // Create a debounced update function
  const debouncedUpdate = useMemo(
    () =>
      debounce((content) => {
        updateScript({ projectId, content })
          .unwrap()
          .then(() => {
            setSaveStatus({ message: "Script saved", type: "success" });
          })
          .catch((error) => {
            console.error(error);
            setSaveStatus({ message: "Error saving script", type: "error" });
          });
      }, 2000),
    [projectId, updateScript]
  );

  const handleChange = (e) => {
    const newContent = e.target.value;
    setScriptContent(newContent);
    debouncedUpdate(newContent);
  };

  if (isLoading) {
    return (
      <Box>
        <Text>Loading script...</Text>
      </Box>
    );
  }

  return (
    <VStack align="start" h="100%" spacing={2}>
      <HStack spacing={2}>
        <Heading size="md">Script</Heading>
        {!!saveStatus.message && saveStatus.type === "success" && (
          <Badge
            alignItems="center"
            colorScheme="green"
            display="flex"
            gap={1}
            transition="opacity 0.2s"
          >
            <CheckIcon boxSize={2} />
            Script saved
          </Badge>
        )}
        {!!saveStatus.message && saveStatus.type === "error" && (
          <Badge colorScheme="red" transition="opacity 0.2s">
            Error saving
          </Badge>
        )}
      </HStack>
      <Textarea
        flexGrow={1}
        fontFamily="monospace"
        fontSize="xs"
        onChange={handleChange}
        placeholder="Enter your script here..."
        resize="none"
        size="sm"
        value={scriptContent}
      />
    </VStack>
  );
};

export default Script;
